<template>
  <div class="pa-2 bg-grey-lighten-4 rounded-lg">
    <v-container class="pa-0 pb-2">
      <v-row>
        <v-col class="text-primary">
          {{ props.label }}
        </v-col>
      </v-row>
      <v-row v-if="!isValid">
        <v-col>
          <!-- v-input ensures global medication rules are bubbled up to the parent form -->
          <v-input :error-messages="errorMessages" :error="!isValid" :model-value="props.modelValue"></v-input>
        </v-col>
      </v-row>
    </v-container>
    <v-container v-for="(med, ix) in props.modelValue" :key="ix" class="pa-0 pb-8">
      <v-row>
        <v-col class="text-primary"> {{ ix + 1 }}. {{ med.name }} </v-col>
        <v-col cols="12">
          <v-btn-lh v-if="!props.disabled" variant="outlined" color="error" class="text-truncate" @click="onDelete(ix)">
            Remove {{ med.name?.split(" ")[0] }}
          </v-btn-lh>
        </v-col>
        <v-col v-if="med.metaData" cols="12">
          <InputMetadata
            :metadata="med.metaData"
            :label="med.name ?? 'Medication'"
            @highlight-bounding-box="onHighlightBoundingBox" />
        </v-col>
      </v-row>
      <br />
      <v-row v-if="!isFieldHidden('medication.name')">
        <v-col>
          <RemoteAutocomplete
            :remote-search="(searchTerm) => getClientMedications(ix, searchTerm)"
            :model-value="med.name ?? ''"
            :allow-free-text="true"
            :disabled="props.disabled"
            :label="props.label"
            :rules="
              getFieldRules('medication.name', [
                requiredRule,
                ...(hasNameValidation
                  ? [(v, remoteObj) => valueInItemsAutocompleteRule(v, 'Invalid Medication', remoteObj)]
                  : []),
              ])
            "
            :component-bind="{
              noFilter: true,
              label: getFieldLabel('medication.name', 'Medication Name'),
            }"
            @update:model-value="(v) => onUpdateModel(med, 'name', v, ix)"></RemoteAutocomplete>
        </v-col>
        <v-col v-if="!isFieldHidden('medication.deliveryType')">
          <v-combobox
            ref="deliveryTypeRefs"
            v-tooltip:top="getTooltipDisplayValue(med.deliveryType)"
            :label="getFieldLabel('medication.deliveryType', 'Delivery Type')"
            :model-value="med.deliveryType"
            :disabled="props.disabled"
            :rules="
              getFieldRules(
                'medication.deliveryType',
                hasRouteValidation
                  ? [
                      (value) =>
                        valueInItemsRule(
                          value,
                          clientConfig.value?.intake?.dropdownFields?.['medication.deliveryType'] ??
                            routesByName[med.name]
                        ),
                    ]
                  : []
              )
            "
            :items="routesByName[med.name]"
            @update:model-value="(v) => onUpdateModel(med, 'deliveryType', v, ix)"></v-combobox>
        </v-col>
      </v-row>
      <v-row v-if="medicationNameWarnings[ix]">
        <v-col>
          <v-alert type="warning" variant="tonal" density="compact" class="text-body-2">
            {{ medicationNameWarnings[ix] }}
          </v-alert>
        </v-col>
      </v-row>
      <v-row v-if="!isFieldHidden('medication.dosage')">
        <v-col>
          <v-text-field
            v-tooltip:top="getTooltipDisplayValue(med.dosage)"
            :label="getFieldLabel('medication.dosage', 'Dosage')"
            :model-value="med.dosage"
            :disabled="props.disabled"
            :rules="getFieldRules('medication.dosage')"
            @update:model-value="(v) => onUpdateModel(med, 'dosage', v, ix)"></v-text-field>
        </v-col>
        <v-col v-if="!isFieldHidden('medication.units')">
          <v-combobox
            ref="unitsRefs"
            v-tooltip:top="getTooltipDisplayValue(med.units)"
            :label="getFieldLabel('medication.units', 'Units')"
            :model-value="med.units"
            :disabled="props.disabled"
            :rules="
              getFieldRules(
                'medication.units',
                hasUnitValidation
                  ? [
                      (value) =>
                        valueInItemsRule(
                          value,
                          clientConfig.value?.intake?.dropdownFields?.['medication.units'] ?? unitsByName[med.name]
                        ),
                    ]
                  : []
              )
            "
            validate-on="eager"
            :items="unitsByName[med.name]"
            @update:model-value="(v) => onUpdateModel(med, 'units', v, ix)"></v-combobox>
        </v-col>
      </v-row>
      <v-row v-if="isExtraField('medication.therapyType') && !isFieldHidden('medication.therapyType')">
        <v-col>
          <component
            :is="therapyTypeFieldConfig.component"
            v-tooltip:top="getTooltipDisplayValue(med.therapyType)"
            :label="getFieldLabel('medication.therapyType', 'Therapy Type')"
            :model-value="med.therapyType"
            :disabled="props.disabled"
            :items="therapyTypeFieldConfig.items"
            :rules="getFieldRules('medication.therapyType', therapyTypeFieldConfig.additionalRules)"
            @update:model-value="(v) => onUpdateModel(med, 'therapyType', v, ix)"></component>
        </v-col>
      </v-row>
      <v-row v-if="isExtraField('medication.frequency') && !isFieldHidden('medication.frequency')">
        <v-col>
          <component
            :is="frequencyFieldConfig.component"
            v-tooltip:top="getTooltipDisplayValue(med.frequency)"
            :label="getFieldLabel('medication.frequency', 'Frequency [SIG]')"
            :model-value="med.frequency"
            :disabled="props.disabled"
            :items="frequencyFieldConfig.items"
            :rules="getFieldRules('medication.frequency', frequencyFieldConfig.additionalRules)"
            @update:model-value="(v) => onUpdateModel(med, 'frequency', v, ix)"></component>
        </v-col>
      </v-row>
      <v-row v-if="isExtraField('medication.daw') && !isFieldHidden('medication.daw')">
        <v-col>
          <component
            :is="dawFieldConfig.component"
            v-tooltip:top="getTooltipDisplayValue(med.daw)"
            :label="getFieldLabel('medication.daw', 'DAW')"
            :model-value="med.daw"
            :disabled="props.disabled"
            :items="dawFieldConfig.items"
            :rules="getFieldRules('medication.daw', dawFieldConfig.additionalRules)"
            @update:model-value="(v) => onUpdateModel(med, 'daw', v, ix)"></component>
        </v-col>
      </v-row>
      <v-row v-if="isExtraField('medication.duration') && !isFieldHidden('medication.duration')">
        <v-col>
          <v-text-field
            v-tooltip:top="getTooltipDisplayValue(med.duration)"
            :label="getFieldLabel('medication.duration', 'Duration of Therapy in Days')"
            :model-value="med.duration"
            :disabled="props.disabled"
            :rules="getFieldRules('medication.duration', [isNumericRule])"
            @update:model-value="(v) => onUpdateModel(med, 'duration', v, ix)"></v-text-field>
        </v-col>
      </v-row>
      <v-row
        v-if="
          (isExtraField('medication.frequencyValue') && !isFieldHidden('medication.frequencyValue')) ||
          (isExtraField('medication.frequencyUnit') && !isFieldHidden('medication.frequencyUnit'))
        ">
        <v-col v-if="isExtraField('medication.frequencyValue') && !isFieldHidden('medication.frequencyValue')">
          <v-text-field
            v-tooltip:top="getTooltipDisplayValue(med.frequencyValue)"
            :label="getFieldLabel('medication.frequencyValue', 'Frequency')"
            :model-value="med.frequencyValue"
            :disabled="props.disabled"
            :rules="getFieldRules('medication.frequencyValue', [isIntegerRule])"
            @update:model-value="(v) => onUpdateModel(med, 'frequencyValue', v, ix)"></v-text-field>
        </v-col>
        <v-col v-if="isExtraField('medication.frequencyUnit') && !isFieldHidden('medication.frequencyUnit')">
          <v-combobox
            v-tooltip:top="getTooltipDisplayValue(med.frequencyUnit)"
            :label="getFieldLabel('medication.frequencyUnit', 'Frequency Unit')"
            :model-value="med.frequencyUnit"
            :disabled="props.disabled"
            :items="['days', 'weeks', 'months', 'years']"
            :rules="
              getFieldRules('medication.frequencyUnit', [
                (v) => valueInItemsRule(v, ['days', 'weeks', 'months', 'years']),
              ])
            "
            @update:model-value="(v) => onUpdateModel(med, 'frequencyUnit', v, ix)"></v-combobox>
        </v-col>
      </v-row>
      <v-row
        v-if="
          (isExtraField('medication.durationValue') && !isFieldHidden('medication.durationValue')) ||
          (isExtraField('medication.durationUnit') && !isFieldHidden('medication.durationUnit'))
        ">
        <v-col v-if="isExtraField('medication.durationValue') && !isFieldHidden('medication.durationValue')">
          <v-text-field
            v-tooltip:top="getTooltipDisplayValue(med.durationValue)"
            :label="getFieldLabel('medication.durationValue', 'Duration')"
            :model-value="med.durationValue"
            :disabled="props.disabled"
            :rules="getFieldRules('medication.durationValue', [isIntegerRule])"
            @update:model-value="(v) => onUpdateModel(med, 'durationValue', v, ix)"></v-text-field>
        </v-col>
        <v-col v-if="isExtraField('medication.durationUnit') && !isFieldHidden('medication.durationUnit')">
          <v-combobox
            v-tooltip:top="getTooltipDisplayValue(med.durationUnit)"
            :label="getFieldLabel('medication.durationUnit', 'Duration Unit')"
            :model-value="med.durationUnit"
            :disabled="props.disabled"
            :items="['days', 'weeks', 'months', 'years']"
            :rules="
              getFieldRules('medication.durationUnit', [
                (v) => valueInItemsRule(v, ['days', 'weeks', 'months', 'years']),
              ])
            "
            @update:model-value="(v) => onUpdateModel(med, 'durationUnit', v, ix)"></v-combobox>
        </v-col>
      </v-row>
      <v-row
        v-if="
          (isExtraField('medication.maxNumTreatments') && !isFieldHidden('medication.maxNumTreatments')) ||
          !isFieldHidden('medication.ndc11')
        ">
        <v-col v-if="isExtraField('medication.maxNumTreatments') && !isFieldHidden('medication.maxNumTreatments')">
          <v-text-field
            v-tooltip:top="getTooltipDisplayValue(med.maxNumTreatments)"
            :label="getFieldLabel('medication.maxNumTreatments', 'Max Number of Treatments')"
            :model-value="med.maxNumTreatments"
            :disabled="props.disabled"
            :rules="getFieldRules('medication.maxNumTreatments', [isIntegerRule])"
            @update:model-value="(v) => onUpdateModel(med, 'maxNumTreatments', v, ix)"></v-text-field>
        </v-col>
        <v-col v-if="props.isPrimaryMedication">
          <v-text-field
            :key="`ndc11-${ndc11Rules.length}`"
            v-tooltip:top="getTooltipDisplayValue(med.ndc11)"
            :label="getFieldLabel('medication.ndc11', 'National Drug Code (11 digits)')"
            :model-value="med.ndc11"
            :rules="ndc11Rules"
            :disabled="props.disabled"
            @update:model-value="(v) => onUpdateModel(med, 'ndc11', v, ix)"></v-text-field>
        </v-col>
        <v-col v-else>
          <v-text-field
            :key="`ndc11-${ndc11Rules.length}`"
            v-tooltip:top="getTooltipDisplayValue(med.ndc11)"
            :label="getFieldLabel('medication.ndc11', 'National Drug Code (11 digits)')"
            :model-value="med.ndc11"
            :disabled="props.disabled"
            @update:model-value="(v) => onUpdateModel(med, 'ndc11', v, ix)"></v-text-field>
        </v-col>
      </v-row>
      <v-row
        v-if="
          isExtraField('medication.drugQuantity') &&
          !isFieldHidden('medication.drugQuantity') &&
          props.isPrimaryMedication
        ">
        <v-col>
          <v-text-field
            v-if="isExtraField('medication.drugQuantity')"
            v-tooltip:top="getTooltipDisplayValue(med.drugQuantity)"
            :label="getFieldLabel('medication.drugQuantity', 'Quantity (numeric value)')"
            :model-value="med.drugQuantity"
            :disabled="props.disabled"
            :rules="getFieldRules('medication.drugQuantity', [isNumericRule])"
            @update:model-value="(v) => onUpdateModel(med, 'drugQuantity', v)"></v-text-field>
        </v-col>
      </v-row>
      <v-row
        v-if="
          isExtraField('medication.packageSizeUnit') &&
          !isFieldHidden('medication.packageSizeUnit') &&
          props.isPrimaryMedication
        ">
        <v-col>
          <v-combobox
            v-tooltip:top="getTooltipDisplayValue(med.packageSizeUnit)"
            :label="getFieldLabel('medication.packageSizeUnit', 'Confirm dosage form')"
            :model-value="med.packageSizeUnit"
            :disabled="props.disabled"
            :rules="
              getFieldRules('medication.packageSizeUnit', [
                (value) => valueInItemsRule(value, clientConfig?.intake?.packageSizeUnits || []),
              ])
            "
            :items="clientConfig?.intake?.packageSizeUnits || []"
            @update:model-value="(v) => onUpdateModel(med, 'packageSizeUnit', v, ix)"></v-combobox>
        </v-col>
      </v-row>
      <v-row>
        <v-col v-for="field in props.fields" :key="field.key" :cols="field.colSpan">
          <component
            :is="field.component"
            v-tooltip:top="getTooltipDisplayValue(med[field.key])"
            :label="field.label"
            :model-value="med[field.key]"
            :disabled="props.disabled"
            :rules="field.rules || getFieldRules(field.key)"
            v-bind="field.vBind"
            v-on="field.vOn"
            @update:model-value="(v) => onUpdateModel(med, field.key, v, ix)"></component>
        </v-col>
      </v-row>
      <v-row>
        <v-col v-for="field in props.medicationSpecificFields?.[ix] ?? []" :key="field.key" :cols="field.colSpan">
          <component
            :is="field.component"
            v-tooltip:top="getTooltipDisplayValue(med[field.key])"
            :label="field.label"
            :model-value="med[field.key]"
            :disabled="props.disabled"
            :rules="field.rules || getFieldRules(field.key)"
            v-bind="field.vBind"
            v-on="field.vOn"
            @update:model-value="(v) => onUpdateModel(med, field.key, v, ix)"></component>
        </v-col>
      </v-row>
      <v-row v-if="!isFieldHidden('medication.notes')">
        <v-col>
          <v-textarea
            v-tooltip:top="getTooltipDisplayValue(med.notes)"
            auto-grow
            rows="2"
            :label="getFieldLabel('medication.notes', 'Notes')"
            :model-value="med.notes"
            :disabled="props.disabled"
            :rules="getFieldRules('medication.notes')"
            @update:model-value="(v) => onUpdateModel(med, 'notes', v)"></v-textarea>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-divider></v-divider>
        </v-col>
      </v-row>
    </v-container>
    <v-container>
      <v-row>
        <v-col>
          <v-btn-lh v-if="!props.disabled" @click="onAdd">+ Add Another Medication</v-btn-lh>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup>
import { computed, watch, ref, nextTick } from "vue";
import { useIntakeStore, useRequirementsStore } from "@/stores";
import {
  requiredRule,
  lengthRule,
  isIntegerRule,
  isNumericRule,
  valueInItemsRule,
  fixFieldIfNeeded,
  valueInItemsAutocompleteRule,
} from "@/utils/form";
import { IntakeInsuranceCard } from "@/models/intake";
import RemoteAutocomplete from "@/components/RemoteAutocomplete.vue";
import { transformDropdownFieldsByClientConfig } from "@/utils/clientConfig";
import InputMetadata from "@/components/intakeReview/InputMetadata.vue";
import { useFieldTooltip } from "@/composables/useFieldTooltip";

const { getTooltipDisplayValue } = useFieldTooltip();

const emit = defineEmits(["update:model-value", "highlightBoundingBox"]);
const props = defineProps({
  modelValue: {
    type: Array,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    default: "Medication",
  },
  selectedInsuranceCard: {
    type: IntakeInsuranceCard,
    required: false,
    default: null,
  },
  isPrimaryMedication: {
    type: Boolean,
    default: false,
  },
  getClientMedications: {
    type: Function,
    required: false,
    default: null,
  },
  rules: {
    type: Array,
    default: () => [],
  },
  requiredFields: {
    type: Array,
    default: () => [],
  },
  extraFields: {
    type: Array,
    default: () => [],
  },
  hideFields: {
    type: Array,
    default: () => [],
  },
  validations: {
    type: Array,
    default: () => [],
  },
  renameFields: {
    type: Object,
    default: () => ({}),
  },
  fields: {
    type: [Array, null],
    default: null,
  },
  medicationSpecificFields: {
    type: [Array, null],
    default: null,
    description:
      "Fields that are specific to the medication, such as consecutive days, etc. Should be an array of field arrays. Should track the same order as the medications array.",
  },
});

const deliveryTypeRefs = ref([]);
const unitsRefs = ref([]);

// Simple validation that integrates with v-form
const isValid = ref(true);
const errorMessages = ref([]);

const intakeStore = useIntakeStore();

const validate = () => {
  errorMessages.value = [];
  for (const rule of props.rules) {
    const result = rule(props.modelValue);
    if (result !== true) {
      errorMessages.value.push(result);
      isValid.value = false;
      return false;
    }
  }
  isValid.value = true;
  return true;
};

// Run validation when modelValue changes
watch(() => props.modelValue, validate, { deep: true });

// Run validation when rules change (for eager validation)
watch(() => props.rules, validate, { immediate: true });

defineExpose({
  deliveryTypeRefs,
  unitsRefs,
  validate,
  isValid,
  errorMessages,
});

const fetchedMedications = ref([]); // Store the fetched medication names for each medication
const fetchedRoutes = ref({});
const routesByName = computed(() => {
  const routes = {};
  props.modelValue.forEach((med) => {
    // Flatten the routes per medication
    const thisRoute = fetchedRoutes.value[med.name]?.map((r) => r.route) ?? [];
    // Use dropdown_fields configuration if available, otherwise use default fallback
    const defaultRoutes = clientConfig.value?.intake?.dropdownFields?.["medication.deliveryType"] ?? [
      "Oral",
      "Intravenous",
      "Subcutaneous",
      "Intramuscular",
    ];
    // Extract values from dropdown configuration objects
    const dropdownRoutes = Array.isArray(defaultRoutes)
      ? defaultRoutes.map((item) => (typeof item === "object" ? item.value : item))
      : defaultRoutes;
    routes[med.name] = thisRoute.length ? thisRoute : dropdownRoutes;
  });
  return routes;
});
const unitsByName = computed(() => {
  const units = {};
  props.modelValue.forEach((med) => {
    // Flatten the units per medication, based on selected delivery type
    const thisUnits = fetchedRoutes.value[med.name]?.find((r) => r.route === med.deliveryType)?.units ?? [];
    // Use dropdown_fields configuration if available, otherwise use default fallback
    const defaultUnits = clientConfig.value?.intake?.dropdownFields?.["medication.units"] ?? ["mg", "ml", "units"];
    // Extract values from dropdown configuration objects
    const dropdownUnits = Array.isArray(defaultUnits)
      ? defaultUnits.map((item) => (typeof item === "object" ? item.value : item))
      : defaultUnits;
    units[med.name] = thisUnits.length ? thisUnits : dropdownUnits;
  });
  return units;
});
const clientConfig = computed(() => useRequirementsStore().clientConfig);
const hasNameValidation = computed(() => {
  return props.validations.includes("medication.name") || false;
});
const hasUnitValidation = computed(() => {
  return props.validations.includes("medication.units") || false;
});
const hasRouteValidation = computed(() => {
  return props.validations.includes("medication.route") || false;
});

// Helper functions for client config
const isFieldRequired = (fieldKey) => {
  return props.requiredFields.includes(fieldKey) || false;
};

const getFieldLabel = (fieldKey, defaultLabel) => {
  return props.renameFields?.[fieldKey] || defaultLabel;
};

const getFieldRules = (fieldKey, defaultRules = []) => {
  const rules = [...defaultRules];
  if (isFieldRequired(fieldKey)) {
    rules.push(requiredRule);
  }
  return rules;
};

const isFieldHidden = (fieldKey) => {
  return props.hideFields.includes(fieldKey) || false;
};

const isExtraField = (fieldKey) => {
  return props.extraFields.includes(fieldKey) || false;
};

const getFieldComponent = (fieldKey, defaultComponent = "v-text-field") => {
  // Create a field object similar to what PageFieldsForm uses
  const field = {
    key: fieldKey,
    component: defaultComponent,
  };

  // Apply the same transformation pipeline used in PageFieldsForm
  const transformedFields = transformDropdownFieldsByClientConfig([field], clientConfig.value || {});
  const transformedField = transformedFields[0];

  // Return component configuration
  return {
    component: transformedField.cmp?.component || defaultComponent,
    items: transformedField.cmp?.vBind?.items || null,
    additionalRules: transformedField.cmp?.vBind?.rules || [],
  };
};

const frequencyFieldConfig = computed(() => getFieldComponent("medication.frequency"));
const therapyTypeFieldConfig = computed(() => getFieldComponent("medication.therapyType"));
const dawFieldConfig = computed(() => getFieldComponent("medication.daw"));

const ndc11Rules = computed(() => {
  const rules = [lengthRule(11)];
  if (props.requiredFields?.includes("medication.ndc11")) {
    rules.push(requiredRule);
  } else if (intakeStore.intakeOrder?.insuranceCard?.benefitType === "PHARMACY") {
    rules.push(requiredRule);
  }
  return rules;
});

const medicationNameWarnings = computed(() => {
  if (!clientConfig.value?.intake?.warnings?.includes("medication.name")) {
    return [];
  }
  const warnings = [];
  props.modelValue.forEach((med, ix) => {
    const warning = fetchedMedications.value[ix]?.some((m) => m.value === med.name)
      ? null
      : "This medication is not part of your integration. It will not be synced to your integration.";
    warnings.push(warning);
  });
  return warnings;
});

// Add watcher for initial load
watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue) {
      newValue.forEach((med) => {
        if (med.ndc11) {
          med.ndc11 = med.ndc11.replace(/-/g, "");
        }
      });
      nextTick(() => {
        validateRefFields();
      });
    }
  },
  { immediate: true, deep: true }
);

const validateRefFields = () => {
  deliveryTypeRefs.value.forEach((ref) => {
    ref.validate();
  });
  unitsRefs.value.forEach((ref) => {
    ref.validate();
  });
};

const getClientMedications = async (medIx, searchTerm) => {
  const medications = await props.getClientMedications(searchTerm);
  fetchedMedications.value[medIx] = medications;
  medications.forEach((m) => {
    fetchedRoutes.value[m.title] = m.routes ?? [];
  });
  return medications;
};

const onUpdateModel = (med, key, value, ix) => {
  const fetched = fetchedMedications.value[ix]?.find((m) => m.value === value);
  if (key === "ndc11") {
    value = value.replace(/-/g, "");
  }
  if (typeof value === "object" && value && value.value) {
    value = value.value;
  }
  // Emit a keyup since the pagepreview requires the key
  med[key] = value;
  emit("update:model-value", [...props.modelValue], { fetchedMedication: fetched });
};

const onAdd = () => {
  const meds = [...props.modelValue, {}];
  emit("update:model-value", meds);
};

const onDelete = (ix) => {
  const meds = props.modelValue.filter((_, i) => i !== ix);
  emit("update:model-value", meds);
};

const onHighlightBoundingBox = (metadata) => {
  emit("highlightBoundingBox", metadata);
};

const fixMedicationDropdownErrors = () => {
  props.modelValue.forEach((med, ix) => {
    fixFieldIfNeeded(
      med.units,
      clientConfig.value?.intake?.dropdownFields?.["medication.units"] ?? unitsByName[med.name],
      (v) => onUpdateModel(med, "units", v, ix)
    );
    fixFieldIfNeeded(
      med.deliveryType,
      clientConfig.value?.intake?.dropdownFields?.["medication.deliveryType"] ?? routesByName[med.name],
      (v) => onUpdateModel(med, "deliveryType", v, ix)
    );

    if (props.isPrimaryMedication) {
      fixFieldIfNeeded(med.packageSizeUnit, clientConfig.value?.intake?.packageSizeUnits, (v) =>
        onUpdateModel(med, "packageSizeUnit", v, ix)
      );
    }

    const timeUnits = ["days", "weeks", "months", "years"];
    fixFieldIfNeeded(med.frequencyUnit, timeUnits, (v) => onUpdateModel(med, "frequencyUnit", v, ix));
    fixFieldIfNeeded(med.durationUnit, timeUnits, (v) => onUpdateModel(med, "durationUnit", v, ix));
  });
};

// Watch for client config to become available and run OCR corrections
watch(
  () => [clientConfig.value, props.modelValue],
  ([newClientConfig, newModelValue], oldValues) => {
    const oldClientConfig = oldValues?.[0] || null;

    if (newClientConfig && newModelValue && newModelValue.length > 0 && !oldClientConfig) {
      fixMedicationDropdownErrors();
    }
  },
  { immediate: true, deep: true }
);
</script>
